import { ExpandMore, WarningAmber } from "@mui/icons-material"
import { Button, Chip, Stack, Typography } from "@mui/material"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import { LockerMigrationState } from "./lockerMigrationOverride"
import { MIGRATION_STATE_COLOR } from "./SaLockerMigrationPairDiagram"

/**
 * The always-visible summary line of the override panel: what state the locker
 * is in, whether users can see it, and the toggle that opens the form. On a
 * healthy locker this is the entire footprint of the feature on the page.
 */
export const SaLockerMigrationHeader: FC<{
  currentState: LockerMigrationState
  hidden: boolean
  /**
   * Hidden by the OPERATOR switch rather than by a migration flag. Reported
   * separately because the two are fixed from different panels: telling an
   * operator "hidden by the migration flags" when it is actually their own
   * service switch sends them to rewrite migration state for no reason.
   */
  outOfService: boolean
  incoherent: boolean
  open: boolean
  onToggle: () => void
}> = ({ currentState, hidden, outOfService, incoherent, open, onToggle }) => {
  const { t } = useTranslation()

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      alignItems={{ xs: "flex-start", sm: "center" }}
      justifyContent="space-between"
      sx={{ p: 2.5 }}
    >
      <Stack direction="column" spacing={0.5} sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          {incoherent && <WarningAmber sx={{ fontSize: 17, color: "warning.main" }} />}
          {/* No title here: the console section that hosts this panel is already
              headed "SSTv2 migration", and repeating it twice in 40px of
              vertical space is chrome describing chrome. The state chip IS the
              header — it is the only thing an operator scans for. */}
          <Chip
            size="small"
            variant="outlined"
            color={MIGRATION_STATE_COLOR[currentState]}
            label={t(`lockerMigrationOverride.state.${currentState}`)}
            sx={{ height: 20, fontSize: "0.68rem" }}
          />
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {/* Operator hide is reported FIRST: when both are in play it is the one
              the operator can undo here and now, and the flags underneath may be
              perfectly correct. */}
          {outOfService
            ? t("lockerMigrationOverride.headerOutOfService")
            : hidden
              ? t("lockerMigrationOverride.headerHidden")
              : t("lockerMigrationOverride.headerVisible")}
        </Typography>
      </Stack>

      <Button
        size="small"
        onClick={onToggle}
        endIcon={
          <ExpandMore
            sx={{
              transform: open ? "rotate(180deg)" : "none",
              transition: "transform 150ms",
            }}
          />
        }
        sx={{ textTransform: "none", flexShrink: 0 }}
      >
        {open ? t("cancel") : t("lockerMigrationOverride.openOverride")}
      </Button>
    </Stack>
  )
}

export default SaLockerMigrationHeader
